import React, { PureComponent } from 'react';

import qs from 'qs';
import { FormattedMessage } from 'react-intl';

import Button from '@atlaskit/button/standard-button';
import { Appearance } from '@atlaskit/button/types';
import InlineDialog from '@atlaskit/inline-dialog';
import { getCloneUrl } from '@atlassian/bitkit-clone-controls';

import RefCopyField from 'src/components/ref-label/src/ref-copy-field/ref-copy-field';
import { Repository } from 'src/components/types';

import messages from './checkout-button.i18n';
import * as styles from './checkout-button.style';

type Props = {
  refName?: string;
  hash?: string;
  repository: Repository;
  onClick: () => void;
};

type SourceTreeButtonProps = {
  refName?: string;
  repository: Repository;
  appearance?: Appearance;
};

type State = {
  isOpen: boolean;
};

const sourceTreeCheckoutUrl = (repository: Repository, refName?: string) => {
  const protocol = 'https';
  if (!refName) {
    return '';
  }

  let ref = refName;
  if (repository) {
    ref = `refs/heads/${ref}`;
  }
  const query = qs.stringify({
    ref,
    cloneUrl: getCloneUrl(protocol, repository),
    type: 'bitbucket',
  });
  return `sourcetree://checkoutRef?${query}`;
};

export const SourceTreeButton = ({
  refName,
  repository,
  appearance,
}: SourceTreeButtonProps): JSX.Element => {
  const sourceTreeText = <FormattedMessage {...messages.sourceTreeAction} />;
  const sourceTreeUrl = sourceTreeCheckoutUrl(repository, refName);
  return (
    <styles.ButtonGroup>
      <Button
        isDisabled={!refName}
        href={sourceTreeUrl}
        appearance={appearance}
      >
        {sourceTreeText}
      </Button>
    </styles.ButtonGroup>
  );
};

export default class CheckoutDialogButton extends PureComponent<Props, State> {
  static defaultProps = {
    /* eslint react/default-props-match-prop-types: "warn" */
    onClick: () => {},
  };

  state = {
    isOpen: false,
  };

  checkOutTarget = () => {
    return this.props.refName || this.props.hash;
  };

  toggleDialog = () => {
    this.setState((prevState: State) => ({
      isOpen: !prevState.isOpen,
    }));
    this.props.onClick();
  };

  handleClose = () => this.setState({ isOpen: false });

  render() {
    const { repository, refName = '' } = this.props;
    const { isOpen } = this.state;
    const buttonText = <FormattedMessage {...messages.checkoutAction} />;

    if (!this.checkOutTarget()) {
      return <Button isDisabled>{buttonText}</Button>;
    }

    const dialogContent = (
      // Temp inline style override AK bug: https://ecosystem.atlassian.net/projects/AK/queues/issue/AK-5837
      <div style={{ textAlign: 'left' }}>
        <FormattedMessage {...messages.checkOutBranchHeading} tagName="h3" />
        <styles.Content>
          <RefCopyField refName={refName} />
          <SourceTreeButton refName={refName} repository={repository} />
        </styles.Content>
      </div>
    );

    return (
      <InlineDialog
        content={dialogContent}
        isOpen={isOpen}
        onClose={this.handleClose}
        placement="bottom-end"
      >
        <Button onClick={this.toggleDialog}>{buttonText}</Button>
      </InlineDialog>
    );
  }
}
